import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Toolbox, Brain, Sparkle, Plus, ArrowsClockwise, CheckCircle, Warning, XCircle,
  Wrench, FlowArrow, GraduationCap, Plug, PlugsConnected,
} from "@phosphor-icons/react";

/* ────────────────────────────────────────────────────────────────────────────
 * Method Laboratory · Propose-and-Audit interface
 *   Operator proposes a category-strategy method.
 *   Strategic Partner runs a Feasibility Trace, naming supported and missing sensors
 *   from the Procureo data stack and laying out concrete next steps.
 * ────────────────────────────────────────────────────────────────────────── */

const FEAS_TONE = {
  FEASIBLE: { fg: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40", icon: CheckCircle },
  PARTIAL:  { fg: "text-amber-300",   bg: "bg-amber-500/10",   border: "border-amber-500/40", icon: Warning },
  BLOCKED:  { fg: "text-rose-300",    bg: "bg-rose-500/10",    border: "border-rose-500/40", icon: XCircle },
};

const TEMPLATE_METHODS = [
  {
    title: "Target tier-2 sub-components for savings",
    hypothesis: "Pushing should-cost down to tier-2 sub-assemblies (BOM-level) will surface 8-12% incremental savings hidden inside fully-integrated supplier quotes.",
    target_outcome: "Recover $4-6M of latent savings on the top-10 Direct categories within two quarters.",
    success_metric: "Tier-2 quote variance vs sealed bid · target ≥ 8% across 12 awards.",
    inputs_required: "BOM tear-downs · supplier tier mapping · should-cost SME runs · FRED commodity feeds.",
  },
  {
    title: "Dynamic supplier consolidation by category",
    hypothesis: "Suppliers carrying < $200K spend across multiple categories cost more in attestation overhead than they save in price.",
    target_outcome: "Compress supplier base by 20% per category without raising counterparty risk above tier 50.",
    success_metric: "Active supplier count · risk-tier weighted exposure.",
    inputs_required: "Supplier exposure ranking · risk score per supplier · category coverage matrix.",
  },
  {
    title: "Geopolitical hedge layer per UNSPSC family",
    hypothesis: "Categories tied to UNSPSC 30 (Electronic Components) carry latent FX + geopolitical risk that the current risk score under-prices.",
    target_outcome: "Surface 3-5 categories where hedge cost < risk-adjusted savings exposure.",
    success_metric: "Risk-adjusted Δ per hedged category vs baseline.",
    inputs_required: "Segment driver map · ECB FX · GDELT event feed · supplier country of origin.",
  },
];

export default function MethodsLabView() {
  const [methods, setMethods] = useState(null);
  const [sensors, setSensors] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const refresh = () =>
    api.methods().then((d) => setMethods(d.methods || [])).catch(() => setMethods([]));

  useEffect(() => {
    refresh();
    api.methodsSensors().then((d) => setSensors(d.sensors || [])).catch(() => setSensors([]));
  }, []);

  const active = useMemo(
    () => (methods || []).find((m) => m.id === activeId) || null,
    [methods, activeId],
  );

  if (!methods || !sensors) {
    return <div data-testid="methods-loading" className="h-[480px] skeleton" />;
  }

  return (
    <section data-testid="methods-lab-view" className="bg-slate-900 border border-slate-800">
      <Header onAdd={() => { setShowForm(true); setActiveId(null); }} count={methods.length} sensorCount={sensors.length} />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-px bg-slate-800">
        {/* List rail */}
        <aside className="xl:col-span-3 bg-slate-950 p-4 min-h-[520px]">
          <div className="flex items-center justify-between mb-2">
            <span className="tracking-mono-label text-[10px] text-slate-500">Proposed Methods</span>
            <span className="font-mono text-[10px] text-slate-500">{methods.length}</span>
          </div>
          <ul className="space-y-1.5 max-h-[480px] overflow-auto thin-scroll" data-testid="methods-list">
            {methods.map((m) => {
              const tone = FEAS_TONE[m.feasibility?.feasibility] || FEAS_TONE.PARTIAL;
              return (
                <li key={m.id}>
                  <button
                    data-testid={`method-row-${m.id}`}
                    onClick={() => { setActiveId(m.id); setShowForm(false); }}
                    className={`w-full text-left p-2.5 rounded-sm border transition-colors ${
                      activeId === m.id
                        ? "border-emerald-500/50 bg-emerald-500/10"
                        : "border-slate-800 hover:border-slate-700 hover:bg-slate-900"
                    }`}
                  >
                    <div className="text-xs text-slate-100 truncate">{m.title}</div>
                    <div className="font-mono text-[10px] text-slate-500 truncate">
                      {m.author} · {new Date(m.created_at).toLocaleDateString()}
                    </div>
                    <div className={`font-mono text-[10px] mt-0.5 ${tone.fg}`}>
                      {m.feasibility?.feasibility || "PARTIAL"} · {Math.round((m.feasibility?.confidence || 0) * 100)}% conf
                    </div>
                  </button>
                </li>
              );
            })}
            {methods.length === 0 && (
              <li className="text-[11px] font-mono text-slate-500 text-center py-6">
                No methods yet. Propose one to audit feasibility against the sensor stack.
              </li>
            )}
          </ul>

          {/* Sensor inventory micro-band */}
          <div className="mt-4 pt-3 border-t border-slate-800">
            <div className="tracking-mono-label text-[10px] text-slate-500 mb-2 flex items-center gap-1.5">
              <PlugsConnected weight="duotone" size={11} /> Sensor inventory · {sensors.length}
            </div>
            <ul className="space-y-1 text-[10px] font-mono text-slate-400 max-h-[220px] overflow-auto thin-scroll">
              {sensors.map((s, i) => (
                <li key={i} data-testid={`sensor-${i}`} className="flex items-baseline gap-1.5 leading-snug">
                  <Plug size={9} className="text-emerald-500 mt-[2px] shrink-0" />
                  <span className="truncate">{s.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <main className="xl:col-span-9 bg-slate-950 min-h-[520px]">
          {showForm
            ? <ProposeForm
                onSubmitted={(m) => { refresh().then(() => setActiveId(m.id)); setShowForm(false); }}
                onCancel={() => setShowForm(false)}
              />
            : active
              ? <FeasibilityDetail method={active} />
              : <EmptyState onAdd={() => setShowForm(true)} />}
        </main>
      </div>
    </section>
  );
}

function Header({ onAdd, count, sensorCount }) {
  return (
    <header className="px-5 py-4 border-b border-slate-800 flex items-baseline justify-between gap-4 flex-wrap">
      <div>
        <h2 className="font-display text-base font-semibold text-slate-50 flex items-center gap-2">
          <Toolbox weight="duotone" className="text-emerald-400" size={16} />
          Methods & Tools · Method Laboratory
        </h2>
        <p className="text-xs text-slate-500 mt-0.5 font-mono">
          Propose a category-strategy method · Strategic Partner audits feasibility against {sensorCount} Procureo sensors.
        </p>
      </div>
      <button
        data-testid="propose-method"
        onClick={onAdd}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10
                   text-emerald-200 font-mono text-xs tracking-mono-label hover:bg-emerald-500/20 transition-colors"
      >
        <Plus weight="bold" size={12} /> propose new method
      </button>
    </header>
  );
}

/* ─── Propose form (with one-click templates) ───────────────────────────── */
function ProposeForm({ onSubmitted, onCancel }) {
  const [form, setForm] = useState({
    title: "", hypothesis: "", target_outcome: "", success_metric: "", inputs_required: "",
    author: "Cameron · Category Lead",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.title.trim() || !form.hypothesis.trim()) {
      setError("Title and hypothesis are required."); return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const m = await api.createMethod(form);
      onSubmitted?.(m);
    } catch (e) {
      setError(e?.message || "Feasibility audit failed");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="p-5 space-y-4" data-testid="method-form">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="font-display text-sm font-semibold text-slate-50 flex items-center gap-2">
            <Sparkle weight="duotone" className="text-emerald-400" size={14} />
            Propose a new method
          </h3>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5">
            Define the hypothesis · Strategic Partner returns supported sensors, missing sensors, recommended steps.
          </p>
        </div>
        <button type="button" onClick={onCancel}
          className="font-mono text-[10px] tracking-mono-label text-slate-400 hover:text-slate-200">
          ✕ cancel
        </button>
      </div>

      {/* Templates */}
      <div className="border border-slate-800 rounded-sm bg-slate-950 p-3">
        <div className="tracking-mono-label text-[10px] text-slate-500 mb-2">Start from a template</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {TEMPLATE_METHODS.map((t, i) => (
            <button
              key={i} type="button"
              data-testid={`method-template-${i}`}
              onClick={() => setForm({ ...form, ...t })}
              className="text-left p-2 rounded-sm border border-slate-800 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors"
            >
              <div className="text-[11px] text-slate-100 leading-snug">{t.title}</div>
              <div className="text-[10px] text-slate-500 mt-0.5 font-mono line-clamp-2">{t.target_outcome}</div>
            </button>
          ))}
        </div>
      </div>

      <Input testid="method-title" label="Method title" value={form.title}
             onChange={(v) => setForm({ ...form, title: v })}
             placeholder="e.g. Target tier-2 sub-components for savings" />

      <Textarea testid="method-hypothesis" label="Hypothesis" rows={3}
                placeholder="What do you believe is true and want to validate?"
                value={form.hypothesis}
                onChange={(v) => setForm({ ...form, hypothesis: v })} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Textarea testid="method-target" label="Target outcome" rows={2}
                  placeholder="The business outcome if the hypothesis is true."
                  value={form.target_outcome}
                  onChange={(v) => setForm({ ...form, target_outcome: v })} />
        <Textarea testid="method-metric" label="Success metric" rows={2}
                  placeholder="The single number you would report to the CFO."
                  value={form.success_metric}
                  onChange={(v) => setForm({ ...form, success_metric: v })} />
      </div>

      <Textarea testid="method-inputs" label="Inputs required" rows={2}
                placeholder="The data / feeds / signals this method needs to run."
                value={form.inputs_required}
                onChange={(v) => setForm({ ...form, inputs_required: v })} />

      <Input testid="method-author" label="Author" value={form.author}
             onChange={(v) => setForm({ ...form, author: v })} />

      {error && <div className="text-rose-300 text-xs font-mono" data-testid="method-error">{error}</div>}

      <div className="flex items-center gap-3">
        <button
          data-testid="submit-method"
          type="submit" disabled={submitting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10
                     text-emerald-200 font-mono text-xs tracking-mono-label hover:bg-emerald-500/20 disabled:opacity-50"
        >
          {submitting
            ? <><ArrowsClockwise size={12} className="animate-spin" /> strategic partner auditing feasibility…</>
            : <><Brain weight="duotone" size={14} /> run feasibility trace</>}
        </button>
        <span className="text-[10px] font-mono text-slate-500">
          On submit · Strategic Partner reads the sensor inventory and returns a feasibility verdict.
        </span>
      </div>
    </form>
  );
}

/* ─── Feasibility detail viewer ─────────────────────────────────────────── */
function FeasibilityDetail({ method }) {
  const f = method.feasibility || {};
  const tone = FEAS_TONE[f.feasibility] || FEAS_TONE.PARTIAL;
  const Icon = tone.icon;

  return (
    <div className="p-5 space-y-4" data-testid={`method-detail-${method.id}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-base font-semibold text-slate-50">{method.title}</h3>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5">
            {method.author} · {new Date(method.created_at).toLocaleString()}
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border ${tone.border} ${tone.bg}`}
             data-testid="feasibility-verdict">
          <Icon weight="fill" className={tone.fg} size={14} />
          <div className="leading-tight">
            <div className={`tracking-mono-label text-[10px] ${tone.fg}`}>
              Feasibility · {f.feasibility} · {Math.round((f.confidence || 0) * 100)}% confidence
            </div>
            <div className="font-mono text-[10px] text-slate-400">Strategic Partner (Lead) · {method.sensor_snapshot?.length || 0} sensors audited</div>
          </div>
        </div>
      </div>

      {/* Hypothesis card */}
      <div className="border border-slate-800 rounded-sm p-3 bg-slate-950">
        <div className="tracking-mono-label text-[10px] text-emerald-300 mb-1 flex items-center gap-1.5">
          <Wrench weight="duotone" size={11} /> Hypothesis
        </div>
        <p className="text-sm text-slate-100 leading-snug">{method.hypothesis}</p>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px] font-mono">
          <Snippet label="Target outcome" value={method.target_outcome} />
          <Snippet label="Success metric" value={method.success_metric} />
          <Snippet label="Inputs required" value={method.inputs_required} />
        </div>
      </div>

      {/* SPLIT pane · Supported vs Missing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-800">
        <div className="bg-slate-950 p-4">
          <div className="tracking-mono-label text-[10px] text-emerald-300 mb-2 flex items-center gap-1.5">
            <PlugsConnected weight="duotone" size={11} /> Supported sensors · {(f.supported_sensors || []).length}
          </div>
          <ul className="space-y-2" data-testid="supported-sensors">
            {(f.supported_sensors || []).map((s, i) => (
              <li key={i} className="border-l-2 border-emerald-500/40 pl-3 py-1" data-testid={`supported-${i}`}>
                <div className="text-[11px] text-slate-100">{s.name}</div>
                <div className="text-[10px] text-slate-400 leading-snug">{s.why}</div>
              </li>
            ))}
            {(f.supported_sensors || []).length === 0 && (
              <li className="text-[11px] font-mono text-slate-500">— none cited</li>
            )}
          </ul>
        </div>
        <div className="bg-slate-950 p-4">
          <div className="tracking-mono-label text-[10px] text-amber-300 mb-2 flex items-center gap-1.5">
            <Plug weight="duotone" size={11} /> Missing sensors · {(f.missing_sensors || []).length}
          </div>
          <ul className="space-y-2" data-testid="missing-sensors">
            {(f.missing_sensors || []).map((s, i) => (
              <li key={i} className="border-l-2 border-amber-500/40 pl-3 py-1" data-testid={`missing-${i}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[11px] text-slate-100">{s.name}</span>
                  {s.source && <span className="text-[10px] font-mono text-amber-300">{s.source}</span>}
                </div>
                <div className="text-[10px] text-slate-400 leading-snug">{s.why}</div>
              </li>
            ))}
            {(f.missing_sensors || []).length === 0 && (
              <li className="text-[11px] font-mono text-slate-500">— no gaps · method ships today</li>
            )}
          </ul>
        </div>
      </div>

      {/* Steps + risks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-800">
        <div className="bg-slate-950 p-4">
          <div className="tracking-mono-label text-[10px] text-emerald-300 mb-2 flex items-center gap-1.5">
            <FlowArrow weight="duotone" size={11} /> Recommended steps
          </div>
          <ol className="space-y-2 list-decimal pl-4 text-[12px] text-slate-200 leading-snug" data-testid="rec-steps">
            {(f.recommended_steps || []).map((s, i) => <li key={i}>{s}</li>)}
            {(f.recommended_steps || []).length === 0 && <li className="text-slate-500">— none cited</li>}
          </ol>
        </div>
        <div className="bg-slate-950 p-4">
          <div className="tracking-mono-label text-[10px] text-rose-300 mb-2 flex items-center gap-1.5">
            <Warning weight="duotone" size={11} /> Risk flags
          </div>
          <ul className="space-y-1.5 text-[11px] text-slate-200 leading-snug" data-testid="risk-flags">
            {(f.risk_flags || []).map((r, i) => <li key={i}>· {r}</li>)}
            {(f.risk_flags || []).length === 0 && <li className="text-slate-500 font-mono">— no risk flags</li>}
          </ul>
        </div>
      </div>

      {/* Strategic Partner trace */}
      {f.opus_trace && (
        <div className="border-l-2 border-fuchsia-500/40 pl-3 py-1">
          <div className="tracking-mono-label text-[10px] text-fuchsia-300 mb-1 flex items-center gap-1.5">
            <Brain weight="duotone" size={11} /> Strategic Partner trace
          </div>
          <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap" data-testid="opus-trace">
            {f.opus_trace}
          </p>
        </div>
      )}
    </div>
  );
}

function Snippet({ label, value }) {
  if (!value) return null;
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-sm p-2">
      <div className="tracking-mono-label text-[9px] text-slate-500">{label}</div>
      <div className="text-[11px] text-slate-100 mt-0.5 leading-snug">{value}</div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="tracking-mono-label text-[10px] text-slate-500">{label}</span>
      <input
        data-testid={testid}
        type="text" value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full bg-slate-950 border border-slate-800 rounded-sm px-2.5 py-1.5
                   text-sm text-slate-100 placeholder:text-slate-600 focus:border-emerald-500/60 outline-none"
      />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, placeholder, testid }) {
  return (
    <label className="block">
      <span className="tracking-mono-label text-[10px] text-slate-500">{label}</span>
      <textarea
        data-testid={testid}
        rows={rows} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full bg-slate-950 border border-slate-800 rounded-sm px-2.5 py-1.5
                   text-sm text-slate-100 placeholder:text-slate-600 focus:border-emerald-500/60 outline-none"
      />
    </label>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-10 text-center" data-testid="methods-empty">
      <Toolbox weight="duotone" className="text-emerald-400/40" size={48} />
      <div className="font-display text-base text-slate-200 mt-3">
        Bring a hypothesis. Strategic Partner will audit feasibility.
      </div>
      <p className="text-xs text-slate-400 font-mono max-w-md mt-2 leading-relaxed">
        The Method Laboratory turns intuition into shippable strategy. Strategic Partner audits the
        Procureo sensor stack and tells you exactly what is supported today and what must
        be built next.
      </p>
      <button onClick={onAdd}
        className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10
                   text-emerald-200 font-mono text-xs tracking-mono-label hover:bg-emerald-500/20">
        <Plus weight="bold" size={12} /> propose new method
      </button>
    </div>
  );
}
